import { normalizeQuery } from '@liveauctioneers/caterwaul-components/lib/InboxMessenger/utils/urls';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import CategoryAd from '@/components/Ads/CategoryAd';
import EnsureLoggedInContainer from '@/components/EnsureLoggedIn/EnsureLoggedInContainer';
import { InboxFolderStats } from '@/components/InboxMessenger/components/Folders/types/InboxFolderStats';
import { Conversations, InboxLayout } from '@/components/InboxMessenger/InboxMessenger';
import PageTitle from '@/components/PageTitle/PageTitle';
import { usePrevious } from '@/hooks/usePrevious';
import useWindowSize from '@/hooks/useWindowSize';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { LeaderBoardAdTypes } from '@/redux/modules/catalog/ad/catalogAd.types';
import { getBaseUrl } from '@/redux/modules/config';
import {
    getConversationPagination,
    getConversations,
    getConversationsLoading,
} from '@/redux/modules/conversation/conversations/conversation.selectors';
import {
    archiveConversationFolder,
    createConversationFolder,
    renameConversationFolder,
} from '@/redux/modules/conversation/folders/conversationFolders.actions';
import { getConversationFolders } from '@/redux/modules/conversation/folders/conversationFolders.selectors';
import { loadConversationPageData } from '@/redux/modules/conversation/page/conversationPage.actions';
import { Folder } from '@/types/Messaging';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { parseQueryParams } from '@/utils/queryParams';
import { getMessagesUrl, toQueryString } from '@/utils/urls';

import MessagesPageHeaderMeta from './MessagesPageHeaderMeta';

const DEFAULT_PAGE_SIZE = 48;
const buttonClasses = 'mb-xs w-[calc(100%-48px-8px)]';

const MessagesPage = () => {
    const baseUrl = useAppSelector(getBaseUrl);
    const conversations = useAppSelector(getConversations);
    const conversationsLoading = useAppSelector(getConversationsLoading);
    const { width } = useWindowSize();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const { formatNumber } = useIntl();

    const queryParams = new URLSearchParams(location.search);
    const activeFolder = queryParams.get('folderId') || 0;
    const params: any = parseQueryParams(location.search);

    const setActiveInbox = (folderId) => {
        const newParams = normalizeQuery({
            ...params,
            filter: 'all',
            folderId,
            page: 1,
            search: '',
        });
        navigate({
            pathname: getMessagesUrl(),
            search: toQueryString(newParams),
        });
    };

    const navigateTo = (param: any) => {
        const newParams = normalizeQuery({ ...params, ...param });
        navigate({ search: toQueryString(newParams) });
    };

    const searchParam = params.search;
    const previousSearch = usePrevious(searchParam);

    const onSearch = (search: string) => {
        const params: any = { search };
        if ((!previousSearch && Boolean(search)) || (Boolean(previousSearch) && !search)) {
            params.page = 1;
        }
        return navigateTo(params);
    };

    const onFilter = (filter: string) => {
        navigateTo({ filter });
    };

    const getMessengerUrl = (convId) => {
        const queryString = toQueryString(params);
        return `${getMessagesUrl()}/${convId}${queryString ? `?${queryString}` : ''}`;
    };

    const { totalRecords } = useAppSelector(getConversationPagination);

    const inboxFolders: InboxFolderStats = useAppSelector(getConversationFolders);
    const folders = useMemo(
        () => [...inboxFolders.systemFolders, ...inboxFolders.userFolders],
        [inboxFolders.systemFolders, inboxFolders.userFolders]
    );

    let numberActiveFolder = null;
    if (!params.search) {
        numberActiveFolder = Number(activeFolder);
    }

    const archiveFolder = useCallback((folderId: number) => dispatch(archiveConversationFolder(folderId)), [dispatch]);

    const createFolder = useCallback((name: string) => dispatch(createConversationFolder(name)), [dispatch]);

    const renameFolder = useCallback(
        (folderId: number, name: string) => dispatch(renameConversationFolder({ folderId, name })),
        [dispatch]
    );

    const convoActionArchive = useCallback(
        (conversationIds) =>
            dispatch(
                loadConversationPageData({
                    action: 'archive',
                    conversationIds,
                    query: params,
                })
            ),
        [dispatch, params]
    );

    const convoActionMarkRead = useCallback(
        (conversationIds) =>
            dispatch(
                loadConversationPageData({
                    action: 'markRead',
                    conversationIds,
                    query: params,
                })
            ),
        [dispatch, params]
    );

    const convoActionMarkUnread = useCallback(
        (conversationIds) =>
            dispatch(
                loadConversationPageData({
                    action: 'markUnread',
                    conversationIds,
                    query: params,
                })
            ),
        [dispatch, params]
    );

    const convoActionMove = useCallback(
        (conversationIds: number[], folder: Folder) => {
            const { id: folderId, name: folderName } = folder;
            dispatch(
                loadConversationPageData({
                    action: 'move',
                    conversationIds,
                    folderId,
                    markOnly: false,
                    query: params,
                })
            );
            toast('success', {
                description: formatMessage(['toastMessages', 'descriptions', 'conversationMovedToFolder'], {
                    values: { folderName },
                }),
                title: formatMessage(['toastMessages', 'titles', 'success']) as unknown as string,
            });
        },
        [dispatch, params, formatMessage]
    );

    const adId = 'messages';
    const adPageName = `leaderboard-${adId}`;

    // Prevent Messenger Page from trying to load SSR
    const [hasRendered, setRendered] = useState(false);
    useEffect(() => setRendered(true), [hasRendered]);

    if (!hasRendered) {
        return null;
    }

    return (
        <EnsureLoggedInContainer>
            <MessagesPageHeaderMeta baseUrl={baseUrl} />
            <CategoryAd
                adType={LeaderBoardAdTypes[adId]}
                pageName={adPageName}
            />
            <PageTitle
                className="mx-auto mb-md mt-lg w-full max-w-[1240px] px-md"
                title={<>{formatMessage(['messaging', 'title'])}</>}
            />
            {width <= 900 && (
                <div className="mx-md flex flex-row items-center justify-start">
                    <Button
                        className={cn(buttonClasses, 'mr-xs')}
                        onClick={() => setActiveInbox(0)}
                        size="sm"
                        variant={numberActiveFolder !== 0 ? 'outlined' : 'filled'}
                    >
                        {formatMessage(['messaging', 'inbox'])}
                    </Button>
                    <Button
                        className={cn(buttonClasses, 'mr-xs')}
                        onClick={() => setActiveInbox(1)}
                        size="sm"
                        variant={numberActiveFolder !== 1 ? 'outlined' : 'filled'}
                    >
                        {formatMessage(['messaging', 'sent'])}
                    </Button>
                    <Button
                        className={cn(buttonClasses)}
                        onClick={() => setActiveInbox(-1)}
                        size="sm"
                        variant={numberActiveFolder !== -1 ? 'outlined' : 'filled'}
                    >
                        {formatMessage(['messaging', 'archive'])}
                    </Button>
                </div>
            )}
            <InboxLayout
                activeFolder={numberActiveFolder}
                className="px-md"
                formatNumber={formatNumber}
                hideLeftNav={width < 900}
                inboxFolders={inboxFolders}
                loading={conversationsLoading}
                onArchiveFolder={archiveFolder}
                onCreateFolder={createFolder}
                onRenameFolder={renameFolder}
                onSearch={onSearch}
                onSetActiveInbox={setActiveInbox}
            >
                <Conversations
                    activeFolder={numberActiveFolder}
                    archiveLabel={formatMessage(['messaging', 'archive'])}
                    // @ts-expect-error
                    // this component uses "ComponentType" but the slice was previously using "any"
                    //   but it was actually hiding the "FetchConversationResponsePayload" type
                    conversations={conversations}
                    conversationsLoading={conversationsLoading}
                    filter={params.filter || 'all'}
                    folders={folders}
                    formatNumber={formatNumber}
                    getMessengerUrl={getMessengerUrl}
                    location={location}
                    markReadLabel={formatMessage(['messaging', 'markRead'])}
                    markUnreadLabel={formatMessage(['messaging', 'markUnread'])}
                    mobile={width < 700}
                    moveLabel={formatMessage(['messaging', 'move'])}
                    onArchive={convoActionArchive}
                    onChangePagination={navigateTo}
                    onFilter={onFilter}
                    onMarkRead={convoActionMarkRead}
                    onMarkUnread={convoActionMarkUnread}
                    onMove={convoActionMove}
                    paginationFilter={{
                        page: params.page || 1,
                        pageSize: params.pageSize || DEFAULT_PAGE_SIZE,
                        totalRecords,
                    }}
                />
            </InboxLayout>
        </EnsureLoggedInContainer>
    );
};

export default MessagesPage;
